import React, { useEffect, useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { FE_URL } from "../../common/constants";
import axiosWithAuth from "../../utils/axiosWithAuth";
import { buildOrderData } from "../../hooks/useCreateOrder";
import { Button, message, Modal } from "antd";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import gsap from "gsap";
import PaymentProcessing from "./PaymentProcessing";

const ORDER_STATUS_POLL_INTERVAL_MS = 2000;
const ORDER_STATUS_TIMEOUT_MS = 60000;

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const pollOrderStatus = async (paymentIntentId) => {
  const statusURL = `/api/orders/status/${encodeURIComponent(paymentIntentId)}`;
  const deadline = Date.now() + ORDER_STATUS_TIMEOUT_MS;

  while (Date.now() < deadline) {
    try {
      const response = await axiosWithAuth().get(statusURL);

      console.log("[Bizzll] Order status response:", response);

      if (response.status === 200) {
        return { result: "success", response };
      }

      return { result: "error" };
    } catch (error) {
      const status = error.response?.status;

      console.log("[Bizzll] Order status polling response:", {
        status,
        response: error.response,
      });

      if (status === 404) {
        const remainingTime = deadline - Date.now();

        if (remainingTime <= 0) {
          break;
        }

        await wait(Math.min(ORDER_STATUS_POLL_INTERVAL_MS, remainingTime));
        continue;
      }

      if (status === 403) {
        return { result: "forbidden" };
      }

      if (status === 500) {
        return { result: "server-error" };
      }

      return { result: "error" };
    }
  }

  return { result: "timeout" };
};

const showOrderStatusError = (result) => {
  const errorMessages = {
    forbidden: "We couldn't verify this order.",
    "server-error": "The server couldn't verify your order.",
    timeout:
      "Your order is still being processed. Please try checking again later.",
    error: "We couldn't verify the status of your order.",
  };

  Modal.error({
    title: "Order verification failed",
    content: errorMessages[result] || errorMessages.error,
  });
};

const getOrderId = (response) => {
  const responseData = response?.data;
  const orderIdKeys = ["orderID", "orderId", "order_id", "id_order", "id"];
  const containers = [
    responseData,
    responseData?.order,
    responseData?.data,
    responseData?.data?.order,
  ];

  for (const container of containers) {
    for (const key of orderIdKeys) {
      if (container?.[key] !== undefined && container?.[key] !== null) {
        return container[key];
      }
    }
  }

  return null;
};

export default function PaymentInformation(props) {
  const {
    user,
    currentStep,
    // customerInfo,
    // client_secret,
    // checkoutType,
    // alreadyActive,
    tosendpayload,
  } = props;
  // Obtén los hooks de Stripe
  const stripe = useStripe(); // Este hook proporciona el objeto Stripe
  const elements = useElements(); // Este hook proporciona el objeto Elements, que contiene los elementos de pago

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForOrder, setIsWaitingForOrder] = useState(false);
  const [collapse, setCollapse] = useState(false);
  const [divClass, setDivClass] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        console.error("[Bizzll] Stripe createPaymentMethod error:", error);
        message.error(error.message || "Error processing payment");
        return;
      }

      const orderData = buildOrderData({
        user,
        cartItems: tosendpayload.cartItems,
        subTotal: tosendpayload.subTotal,
        amountTotal: tosendpayload.amount,
      });

      const paymentIntentPayload = {
        amount: tosendpayload.amount,
        cartItems: tosendpayload.cartItems,
        customerID: tosendpayload.customerID,
        subTotal: tosendpayload.subTotal,
        paymentMethodId: paymentMethod.id,
        orderData,
      };

      console.log(
        "[Bizzll] Stripe payment intent payload to send:",
        paymentIntentPayload,
      );

      const paymentIntentRes = await axiosWithAuth().post(
        "/api/stripe/checkout/paymentIntent",
        paymentIntentPayload,
      );

      console.log(
        "[Bizzll] Stripe payment intent full response:",
        paymentIntentRes,
      );

      const paymentIntent = paymentIntentRes.data;
      console.log("[Bizzll] Stripe payment intent response:", paymentIntent);

      const clientSecret =
        typeof paymentIntent === "string"
          ? paymentIntent
          : paymentIntent?.clientSecret ||
            paymentIntent?.client_secret ||
            paymentIntent?.paymentIntent?.client_secret;

      if (paymentIntent?.error) {
        console.error("[Bizzll] Payment intent error:", paymentIntent.error);
        message.error(
          paymentIntent.message ||
            paymentIntent.error.message ||
            "Error creating payment intent",
        );
        return;
      }

      if (paymentIntent?.requiresNewPaymentMethod) {
        message.error(
          paymentIntent.message || "Please use a different payment method",
        );
        return;
      }

      if (!clientSecret) {
        console.error(
          "[Bizzll] Missing clientSecret in payment intent response:",
          paymentIntent,
        );
        message.error("Missing client secret in payment intent response");
        return;
      }

      const {
        error: confirmError,
        paymentIntent: confirmedPaymentIntent,
      } = await stripe.confirmCardPayment(clientSecret);

      console.log("[Bizzll] Stripe payment confirmation response:", {
        error: confirmError,
        paymentIntent: confirmedPaymentIntent,
      });

      if (confirmError) {
        console.error("[Bizzll] Stripe confirmation error:", confirmError);
        message.error(confirmError.message || "Error confirming payment");
        return;
      }

      const paymentIntentId =
        confirmedPaymentIntent?.id ||
        paymentIntent?.paymentIntentId ||
        paymentIntent?.payment_intent_id ||
        paymentIntent?.id ||
        paymentIntent?.paymentIntent?.id;

      if (!paymentIntentId) {
        console.error(
          "[Bizzll] Missing payment intent ID after confirmation",
          {
            paymentIntentResponse: paymentIntent,
            confirmedPaymentIntent,
          },
        );
        message.error("Missing payment intent ID after confirmation");
        return;
      }

      console.log("[Bizzll] Payment Intent ID:", paymentIntentId);

      setIsWaitingForOrder(true);

      const orderStatus = await pollOrderStatus(paymentIntentId);

      if (orderStatus.result === "success") {
        const orderId = getOrderId(orderStatus.response);

        if (!orderId) {
          console.error(
            "[Bizzll] Missing order ID in successful status response",
            orderStatus.response,
          );
          showOrderStatusError("error");
          return;
        }

        window.location.replace(`${FE_URL}/order-confirmation/${orderId}`);
        return;
      }

      showOrderStatusError(orderStatus.result);
    } catch (error) {
      console.error("[Bizzll] Payment submit failed:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Error processing order. Please try again",
      );
    } finally {
      setIsWaitingForOrder(false);
      setIsSubmitting(false);
    }
  };

  const CARD_ELEMENT_BASE_STYLE = {
    color: "#32325d",
    fontFamily: '"Nunito Sans", Helvetica, sans-serif',
    fontSmoothing: "antialiased",
    fontSize: "16px",
    "::placeholder": {
      color: "#aab7c4",
    },
  };

  const CARD_ELEMENT_INVALID_STYLE = {
    color: "#fa755a",
    iconColor: "#fa755a",
  };

  const CARD_NUMBER_OPTIONS = {
    showIcon: true,
    style: {
      base: CARD_ELEMENT_BASE_STYLE,
      invalid: CARD_ELEMENT_INVALID_STYLE,
    },
  };

  const CARD_EXPIRY_OPTIONS = {
    style: {
      base: CARD_ELEMENT_BASE_STYLE,
      invalid: CARD_ELEMENT_INVALID_STYLE,
    },
  };

  const CARD_CVC_OPTIONS = {
    style: {
      base: CARD_ELEMENT_BASE_STYLE,
      invalid: CARD_ELEMENT_INVALID_STYLE,
    },
  };

  //* Set the divs class
  useEffect(() => {
    let classNames;

    if (currentStep > 3) {
      classNames = "";
      setCollapse(true);

      gsap.to("#payment-information .step-content", {
        height: 0,
        padding: 0,
        duration: 0.3,
      });
    } else if (currentStep < 3) {
      classNames = "";
      setCollapse(true);

      gsap.to("#payment-information .step-content", {
        height: 0,
        padding: 0,
        duration: 0.3,
      });
    } else {
      classNames = "is-active incomplete";
      setCollapse(false);

      gsap.to("#payment-information .step-content", {
        height: "auto",
        padding: 10,
        duration: 0.3,
      });
    }

    setDivClass(classNames);
  }, [currentStep]);

  return (
    <>
      <PaymentProcessing visible={isWaitingForOrder} />

      <div
        id="payment-information"
        className={divClass ? `step-wrapper ${divClass}` : "step-wrapper"}
      >
        <div className="step-heading">
          <div className="icon">
            {collapse && currentStep > 2 ? (
              <FontAwesomeIcon icon={faCheck} />
            ) : (
              "3"
            )}
          </div>

          <div className="title">Payment Method</div>
        </div>

        <div className="step-content">
          <form onSubmit={handleSubmit}>
            <div className="fields">
              <div className="card-element card-element-full">
                <CardNumberElement options={CARD_NUMBER_OPTIONS} />
              </div>

              <div className="card-element card-element-half">
                <CardExpiryElement options={CARD_EXPIRY_OPTIONS} />
              </div>

              <div className="card-element card-element-half">
                <CardCvcElement options={CARD_CVC_OPTIONS} />
              </div>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!stripe}
            >
              Process Order
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
